/**
 * Bare-name → Solar (duotone) resolution for `<Icon name="...">` when the
 * caller passes a token with NO iconset prefix (e.g. "gem", "check",
 * "chevron-down").
 *
 * Icon.tsx used to default any unprefixed name to `lucide:${name}`. Lucide is
 * banned for bare tokens now — Solar duotone is the house default (mirrors
 * the FileTree convention in ./config/solar-map.ts). Explicit `lucide:*`
 * literals already in the codebase are untouched — they keep their colon and
 * never reach this table.
 *
 * Sourced from every bare-name call site found under mini-app/app/** (audit
 * 2026-07-10), plus the shared agent roster (lib/agent-roster.ts) and
 * agents/shared/toolIcons.ts. Re-pick any concept's glyph by editing one
 * line (find slugs via `gem icon <query> --text --set solar`).
 */
export const BARE_ICON_MAP: Record<string, string> = {
  // agent identity — lib/agent-roster.ts ROSTER, every agent avatar in
  // chrome header / switch sheet / desktop tile
  "gem": "solar:stars-bold-duotone",
  "shield": "solar:shield-bold-duotone",
  "heart": "solar:heart-bold-duotone",
  "clapperboard": "solar:clapperboard-bold-duotone",
  "hammer": "solar:sledgehammer-bold-duotone",
  "mic": "solar:microphone-bold-duotone",
  "film": "solar:video-frame-bold-duotone",
  "fish": "solar:waterdrop-bold-duotone",   // no literal "fish" in Solar — aquatic stand-in
  "radar": "solar:radar-bold-duotone",
  "server": "solar:server-bold-duotone",

  // tool icons — agents/shared/toolIcons.ts (TOOL_ICON + ICON)
  "terminal": "solar:command-bold-duotone",
  "book-open": "solar:book-bold-duotone",
  "pencil": "solar:pen-bold-duotone",
  "file-pen-line": "solar:pen-new-square-bold-duotone",
  "search": "solar:magnifer-bold-duotone",
  "folder-search": "solar:folder-bold-duotone",   // no folder+magnifer combo in Solar
  "globe": "solar:global-bold-duotone",
  "download": "solar:download-bold-duotone",
  "wrench": "solar:tuning-bold-duotone",
  "list-checks": "solar:checklist-bold-duotone",
  "box": "solar:box-minimalistic-bold-duotone",
  "ellipsis": "solar:menu-dots-bold-duotone",
  "plug": "solar:socket-bold-duotone",
  "sparkles": "solar:magic-stick-3-bold-duotone",
  "git-branch": "solar:branching-paths-up-bold-duotone",

  // general UI glyphs — kit demo/patterns, pulse, margo, filetree swipe
  "lock": "solar:lock-bold-duotone",
  "zap": "solar:bolt-bold-duotone",
  "play": "solar:play-bold-duotone",
  "pause": "solar:pause-bold-duotone",
  "check": "solar:check-circle-bold-duotone",
  "x": "solar:close-circle-bold-duotone",
  "copy": "solar:copy-bold-duotone",
  "plus": "solar:add-circle-bold-duotone",
  "eraser": "solar:eraser-bold-duotone",
  "trash-2": "solar:trash-bin-trash-bold-duotone",
  "loader-2": "solar:refresh-circle-bold-duotone",
  "chevron-down": "solar:alt-arrow-down-bold-duotone",
  "chevron-right": "solar:alt-arrow-right-bold-duotone",
  "chevron-up": "solar:alt-arrow-up-bold-duotone",
  "arrow-right": "solar:arrow-right-bold-duotone",
  "more-vertical": "solar:menu-dots-bold-duotone",
  "file-text": "solar:file-text-bold-duotone",
  "bar-chart-2": "solar:chart-2-bold-duotone",
  "settings": "solar:settings-bold-duotone",
  "shield-check": "solar:shield-check-bold-duotone",
  "timer": "solar:stopwatch-bold-duotone",
  "clock": "solar:alarm-bold-duotone",
  "cpu": "solar:cpu-bold-duotone",
};

/** Generic glyph for a bare name with no explicit entry above. */
export const BARE_ICON_FALLBACK = "solar:box-minimalistic-bold-duotone";
